<template>
  <div class="section">
    <div class="section-header">
      <div class="section-info">
        <span class="section-title">设置</span>
      </div>
      <div class="section-controls"></div>
    </div>
    <div class="section-content" v-show="expanded">
      <div class="setting-group">
        <div class="group-header">
          <div class="setting-label">全局变量</div>
          <div class="group-actions">
            <button class="header-action" title="复制列表" @click="copyVariables('global')"><ElIcon><Document /></ElIcon></button>
            <button class="header-action" title="添加" @click="$emit('open-add', 'global')"><ElIcon><Plus /></ElIcon></button>
          </div>
        </div>
        <div class="variable-list">
          <div class="variable-item" v-for="variable in globalVariables" :key="variable.key" :class="{ active: selectedGlobalKey === variable.key }" @click="selectedGlobalKey = variable.key">
            <div class="variable-info">
              <div class="variable-key">{{ variable.key }}</div>
              <div class="type-badge">{{ getTypeLabel(variable.type) }}</div>
            </div>
            <div class="variable-actions">
              <ElDropdown trigger="click" placement="bottom-end" popper-class="mflow-dark-dropdown">
                <button class="item-menu-btn" @click.stop><ElIcon><MoreFilled /></ElIcon></button>
                <template #dropdown>
                  <ElDropdownMenu>
                    <ElDropdownItem @click="$emit('open-edit', 'global', variable)">编辑</ElDropdownItem>
                    <ElDropdownItem @click="$emit('remove-global', variable)" class="danger">删除</ElDropdownItem>
                  </ElDropdownMenu>
                </template>
              </ElDropdown>
            </div>
          </div>
        </div>
      </div>

      <div class="setting-group">
        <div class="group-header">
          <div class="setting-label">工作流变量</div>
          <div class="group-actions">
            <button class="header-action" title="复制列表" @click="copyVariables('workflow')"><ElIcon><Document /></ElIcon></button>
            <button class="header-action" title="添加" @click="$emit('open-add', 'workflow')"><ElIcon><Plus /></ElIcon></button>
          </div>
        </div>
        <div class="variable-list">
          <div class="variable-item" v-for="variable in workflowVariables" :key="variable.key" :class="{ active: selectedWorkflowKey === variable.key }" @click="selectedWorkflowKey = variable.key">
            <div class="variable-info">
              <div class="variable-key">{{ variable.key }}</div>
              <div class="type-badge">{{ getTypeLabel(variable.type) }}</div>
            </div>
            <div class="variable-actions">
              <ElDropdown trigger="click" placement="bottom-end" popper-class="mflow-dark-dropdown">
                <button class="item-menu-btn" @click.stop><ElIcon><MoreFilled /></ElIcon></button>
                <template #dropdown>
                  <ElDropdownMenu>
                    <ElDropdownItem @click="$emit('open-edit', 'workflow', variable)">编辑</ElDropdownItem>
                    <ElDropdownItem @click="$emit('remove-workflow', variable)" class="danger">删除</ElDropdownItem>
                  </ElDropdownMenu>
                </template>
              </ElDropdown>
            </div>
          </div>
        </div>
      </div>

      <div class="setting-group">
        <div class="setting-label">系统配置</div>
        <div class="config-options">
          <label class="config-option">
            自动保存
            <ElSwitch :model-value="autoSave" @change="(v: boolean) => $emit('update:auto-save', v)" />
          </label>
          <label class="config-option">
            显示网格
            <ElSwitch :model-value="showGrid" @change="(v: boolean) => $emit('update:show-grid', v)" />
          </label>
          <label class="config-option">
            网格对齐
            <ElSwitch :model-value="snapToGrid" @change="(v: boolean) => $emit('update:snap-to-grid', v)" />
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { ElIcon, Plus, Edit, Close, MoreFilled, ElDropdown, ElDropdownMenu, ElDropdownItem, ElSwitch, Document } from '@/plugins/elementPlus'

defineProps<{
  expanded: boolean
  globalVariables: any[]
  workflowVariables: any[]
  autoSave: boolean
  showGrid: boolean
  snapToGrid: boolean
  getTypeLabel: (type: string) => string
}>()

defineEmits<{
  (e: 'toggle'): void
  (e: 'open-add', scope: 'global' | 'workflow'): void
  (e: 'open-edit', scope: 'global' | 'workflow', variable: any): void
  (e: 'remove-global', variable: any): void
  (e: 'remove-workflow', variable: any): void
  (e: 'update:auto-save', val: boolean): void
  (e: 'update:show-grid', val: boolean): void
  (e: 'update:snap-to-grid', val: boolean): void
}>()

const selectedGlobalKey = ref<string>('')
const selectedWorkflowKey = ref<string>('')

async function copyVariables(scope: 'global' | 'workflow') {
  const list = scope === 'global' ? globalVariables : workflowVariables
  const keys = Array.isArray(list) ? list.map(v => v?.key).filter(Boolean) : []
  try {
    const text = JSON.stringify(keys, null, 2)
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
    } else {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
  } catch {}
}
</script>

<style scoped lang="scss">
.section { margin: 0; border-radius: 6px; background: #1f1f1f; overflow: hidden; display: flex; flex-direction: column; }
.section-header { display: flex; align-items: center; justify-content: space-between; padding: 6px 8px; border-bottom: 1px solid #333333; cursor: pointer; user-select: none; }
.section-info { display: flex; align-items: center; gap: 8px; flex: 1; }
.section-title { color: #cccccc; font-size: 12px; font-weight: 500; }
.section-controls { display: flex; align-items: center; gap: 6px; height: 30px; }
.section-content { padding: 6px; flex: 1; overflow-y: auto; background: #1e1e1e; }
.setting-group { background: #2a2a2a; border: 1px solid #333; border-radius: 6px; padding: 8px; margin-bottom: 8px; }
.group-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.group-actions { display: flex; align-items: center; gap: 6px; }
.setting-label { color: #cccccc; font-size: 12px; font-weight: 500; }
.variable-actions { display: flex; align-items: center; gap: 6px; }
.header-action { background: #333333; border:none;width: 20px; height: 20px; color: #bbb; border-radius: 4px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.header-action:hover { background: #333; color: #fff; border-color: #666; }
.variable-list { display: flex; flex-direction: column; gap: 4px; }
.variable-item { display: flex; align-items: center; justify-content: space-between; padding: 6px 8px; background: #2a2a2a; border: 1px solid transparent; border-radius: 4px; cursor: pointer; }
.variable-item:hover { background: #333; border-color: #444; }
.variable-item.active { background: rgba(0,137,255,0.06); border-color: #0089FF; }
.variable-info { flex: 1; min-width: 0; }
.type-badge { background: #2a2a2a; border: 1px solid #444; color: #bbb; font-size: 10px; border-radius: 10px; padding: 2px 6px; }
.variable-key { color: #cccccc; font-size: 11px; font-weight: 500; }
.item-menu-btn { background: none; border: none; color: #bbb; padding: 4px; border-radius: 4px; cursor: pointer; }
.item-menu-btn:hover { background: #333; color: #fff; border-color: #666; }
.config-options { display: flex; align-items: center; gap: 8px; }
.config-option { display: flex; align-items: center; gap: 6px; color: #bbb; font-size: 11px; }
</style>
